import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { DollarSign, FileText, TrendingUp, AlertCircle, ArrowUpRight, RefreshCw, Receipt, Users, Clock, X } from 'lucide-react';
import axios from 'axios';
import { dateToTimestamp, parseFlexibleDate, todayAsStorageDate } from '../utils/date';
import { formatScheduleAssignment } from '../utils/schedules';
import { useHeader } from '../context/HeaderContext';
import AdminDashboard from './AdminDashboard';
import CollaboratorDashboard from './CollaboratorDashboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const normalizeInsurerName = (value = '') => {
  const insurer = value.trim();
  const normalized = insurer.toLowerCase();

  if (normalized.includes('optima de seguros') || normalized.includes('óptima de seguros')) {
    return 'Óptima de Seguros';
  }
  if (normalized.includes('regional')) {
    return 'La Regional de Seguros';
  }
  if (normalized.includes('fedpa')) {
    return 'Seguros Fedpa';
  }
  if (normalized.includes('seguros global') || normalized.includes('global')) {
    return 'Seguros Global';
  }
  if (normalized.includes('assa')) {
    return 'ASSA';
  }

  return insurer;
};

const isWithinBillingWindow = (invoice) => {
  const insurer = normalizeInsurerName(invoice?.insurer || '');
  const invoiceTs = dateToTimestamp(invoice?.date || invoice?.claimDate);
  if (!invoiceTs) return true;

  const today = new Date();
  today.setHours(23, 59, 59, 999);

  let daysBack = null;
  if (insurer === 'ASSA' || insurer === 'Óptima de Seguros') {
    daysBack = 15;
  } else if (insurer === 'La Regional de Seguros' || insurer === 'Seguros Fedpa') {
    daysBack = 30;
  }

  if (!daysBack) return true;

  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - daysBack);

  return invoiceTs >= start.getTime();
};

const isPendingForBilling = (invoice) =>
  invoice?.status === 'pendiente' && (invoice?.paymentStatus || 'pending') !== 'paid';

const getInvoiceDate = (invoice) => parseFlexibleDate(invoice?.date || invoice?.claimDate);

const getLogicalInvoiceKey = (invoice) => {
  const expediente = String(invoice?.expediente || invoice?.id || '').trim().toLowerCase();
  const insurer = normalizeInsurerName(invoice?.insurer || '').trim().toLowerCase();
  return `${insurer}::${expediente}`;
};

const uniqueLogicalInvoices = (invoiceList = []) => {
  const unique = new Map();
  invoiceList.forEach((invoice) => {
    const key = getLogicalInvoiceKey(invoice);
    if (!unique.has(key)) unique.set(key, invoice);
  });
  return [...unique.values()];
};

const isGeneratedClaimBillingCopy = (invoice) => {
  const observation = String(invoice?.networkObservation || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  return observation.startsWith('reclamo aprobado. copia de')
    || observation === 'reclamo rechazado. se factura monto original.';
};

const getMonthKey = (date) => {
  if (!date) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonthLabel = (monthKey) => {
  if (!monthKey) return '';
  const [year, month] = monthKey.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('es-ES', {
    month: 'long',
    year: 'numeric'
  });
};

const SERVICE_COLORS = ['#0ea5e9', '#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316'];

const SERVICE_CATEGORIES = {
  inspection: 'Inspección',
  tire_change: 'Cambio de llanta',
  fuel: 'Abasto de combustible',
  jump_start: 'Paso de corriente',
  locksmith: 'Cerrajería vehicular',
  tow: 'Grúa',
  ambulance: 'Ambulancia',
  compressor: 'Compresor de aire',
  designated_driver: 'Conductor designado',
  courier: 'Mensajería',
  review: 'Datos por revisar'
};

const formatShortMonthLabel = (monthKey) => {
  if (!monthKey) return '';
  const [year, month] = monthKey.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('es-ES', {
    month: 'short',
    year: 'numeric'
  });
};

const normalizeServiceCategory = (value = '') => {
  const service = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  if (!service) return SERVICE_CATEGORIES.review;

  if (
    service.includes('inspecc')
    || service.includes('inpecc')
    || service.includes('colision')
    || service.includes('accidente')
    || service.includes('hurto')
    || service.includes('robo')
    || service.includes('comprensivo')
    || service.includes('vandalismo')
    || service.includes('dano')
    || service.includes('incendio')
    || service.includes('inundacion')
    || service.includes('atropello')
  ) {
    return SERVICE_CATEGORIES.inspection;
  }

  if (service.includes('llanta')) return SERVICE_CATEGORIES.tire_change;
  if (service.includes('combustible') || service.includes('llenado') || service.includes('gasolina')) return SERVICE_CATEGORIES.fuel;
  if (service.includes('corriente') || service.includes('carga electrica')) return SERVICE_CATEGORIES.jump_start;
  if (service.includes('cerrajer')) return SERVICE_CATEGORIES.locksmith;
  if (service.includes('grua') || service.includes('remolque')) return SERVICE_CATEGORIES.tow;
  if (service.includes('ambulancia')) return SERVICE_CATEGORIES.ambulance;
  if (service.includes('compresor')) return SERVICE_CATEGORIES.compressor;
  if (service.includes('conductor') && service.includes('designado')) return SERVICE_CATEGORIES.designated_driver;
  if (service.includes('mensaj')) return SERVICE_CATEGORIES.courier;

  return SERVICE_CATEGORIES.review;
};

const TechnicianDashboard = () => {
  const { currentUser, isManager, users } = useAuth();
  const { setHeaderContent } = useHeader();
  const [dashboardSummary, setDashboardSummary] = useState({
    totalPayableGross: 0,
    totalAttended: 0,
    totalReclaimed: 0,
    totalResolvedClaims: 0,
    totalBillable: 0,
    uploadedServiceCount: 0,
    monthOptions: [],
    serviceStatsByMonth: {},
    technicianTopByMonth: {},
  });
  const [loans, setLoans] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedServiceMonth, setSelectedServiceMonth] = useState('');
  const [selectedTechnicianMonth, setSelectedTechnicianMonth] = useState(() => getMonthKey(new Date()));
  const [showTodaySchedules, setShowTodaySchedules] = useState(false);

  // Fetch pre-calculated dashboard metrics from the API.
  const fetchInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/invoices/dashboard-summary-legacy`, {
        params: {
          user_id: currentUser?.id || '',
          user_name: currentUser?.name || '',
          is_manager: isManager,
        },
      });
      setDashboardSummary(res.data);
    } catch (e) {
      console.error('Error loading invoices:', e);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, currentUser?.name, isManager]);

  // Fetch loans from API
  const fetchLoans = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/loans`);
      setLoans(res.data);
    } catch (e) {
      console.error('Error loading loans:', e);
    }
  }, []);

  const fetchSchedules = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/schedules`);
      setSchedules(res.data || []);
    } catch (e) {
      console.error('Error loading schedules:', e);
    }
  }, []);

  // Manual refresh function
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchInvoices(), fetchLoans(), fetchSchedules()]);
    } catch (e) {
      console.error('Error refreshing data:', e);
    } finally {
      setRefreshing(false);
    }
  }, [fetchInvoices, fetchLoans, fetchSchedules]);

  useEffect(() => {
    setHeaderContent(
      <div className="flex min-w-0 items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-heading font-bold tracking-tight text-slate-800">
            Bienvenido, {currentUser?.name?.split(' ')[0]}
          </h1>
          <p className="truncate text-sm text-slate-500">
            {isManager ? 'Resumen general del CRM' : 'Tus expedientes asignados'} - {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          title={refreshing ? 'Actualizando...' : 'Actualizar'}
          aria-label={refreshing ? 'Actualizando...' : 'Actualizar'}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>
    );

    return () => setHeaderContent(null);
  }, [currentUser?.name, handleRefresh, isManager, refreshing, setHeaderContent]);

  useEffect(() => {
    fetchInvoices();
    fetchLoans();
    fetchSchedules();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchInvoices();
      fetchLoans();
      fetchSchedules();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchInvoices, fetchLoans, fetchSchedules]);

  const todayScheduleItems = useMemo(() => {
    const today = todayAsStorageDate();
    const currentSchedule = schedules.find((schedule) => (
      schedule.status === 'published'
      && schedule.startDate <= today
      && schedule.endDate >= today
    ));
    if (!currentSchedule) return [];

    const usersById = new Map((users || []).map((user) => [user.id, user]));
    return (currentSchedule.assignments || [])
      .filter((assignment) => assignment.date === today && assignment.code !== 'unassigned')
      .map((assignment) => {
        const user = usersById.get(assignment.userId);
        return {
          userId: assignment.userId,
          name: user?.name || 'Técnico',
          schedule: formatScheduleAssignment(assignment),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [schedules, users]);

  const serviceMonthOptions = useMemo(
    () => dashboardSummary.monthOptions || [],
    [dashboardSummary.monthOptions]
  );
  const technicianMonthOptions = serviceMonthOptions;

  useEffect(() => {
    if (serviceMonthOptions.length === 0) {
      setSelectedServiceMonth('');
      return;
    }

    if (!selectedServiceMonth || !serviceMonthOptions.includes(selectedServiceMonth)) {
      setSelectedServiceMonth(serviceMonthOptions[0]);
    }
  }, [serviceMonthOptions, selectedServiceMonth]);

  useEffect(() => {
    if (technicianMonthOptions.length === 0) {
      setSelectedTechnicianMonth('');
      return;
    }

    if (!selectedTechnicianMonth || !technicianMonthOptions.includes(selectedTechnicianMonth)) {
      setSelectedTechnicianMonth(technicianMonthOptions[0]);
    }
  }, [technicianMonthOptions, selectedTechnicianMonth]);

  const monthlyServiceData = dashboardSummary.serviceStatsByMonth?.[selectedServiceMonth] || [];
  const monthlyServiceTotal = monthlyServiceData.reduce((sum, service) => sum + service.count, 0);

  const pendingLoans = loans.filter((loan) => {
    if (loan.status !== 'pending') return false;
    if (isManager) return true;
    if (!currentUser) return false;

    const currentName = (currentUser.name || '').trim().toLowerCase();
    const loanTechnicianName = (loan.technicianName || '').trim().toLowerCase();

    return (
      loan.technicianId === currentUser.id ||
      (!!currentName && loanTechnicianName === currentName)
    );
  });
  const paidLoans = loans.filter(l => l.status === 'paid');
  const totalDescuentosPendientes = pendingLoans.reduce((acc, loan) => acc + (Number(loan.amount) || 0), 0);
  const totalAPagarBruto = Number(dashboardSummary.totalPayableGross) || 0;
  const totalAPagar = isManager
    ? totalAPagarBruto
    : Math.max(0, totalAPagarBruto - totalDescuentosPendientes);

  const totalReclamados = dashboardSummary.totalReclaimed || 0;
  const totalFacturar = dashboardSummary.totalBillable || 0;
  const totalAtendidos = dashboardSummary.totalAttended || 0;
  const totalReclamosPendientes = totalReclamados;
  const totalReclamosResueltos = dashboardSummary.totalResolvedClaims || 0;

  // Chart Data - by status
  const statusCounts = {
    'Atendidos': totalAtendidos,
    'Reclamados': totalReclamados,
    'A Facturar': totalFacturar
  };

  const barData = Object.entries(statusCounts)
    .filter(([_, count]) => count > 0)
    .map(([name, count]) => ({ name, count }));

  const technicianData = dashboardSummary.technicianTopByMonth?.[selectedTechnicianMonth] || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div
      data-testid="dashboard"
      className="w-full min-w-0 space-y-3 overflow-x-hidden xl:h-full xl:overflow-y-auto xl:pr-1"
    >
      {/* Stats Cards - Bento Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 xl:flex-shrink-0">
        {/* 1. Total a pagar */}
        <div data-testid="stat-facturar" className="min-w-0 bg-white p-2 rounded-2xl shadow-sm border border-slate-200 hover:shadow-lg transition-shadow duration-300">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-slate-500 text-xs font-medium">Total a Pagar</h3>
            <div className="p-1.5 bg-emerald-100 text-emerald-600 rounded-xl">
              <DollarSign size={16} />
            </div>
          </div>
          <p className="text-lg lg:text-[1.35rem] font-heading font-bold text-slate-800 break-words">${totalAPagar.toLocaleString()}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[11px] text-emerald-600 font-medium flex items-center gap-1 bg-emerald-50 px-2 py-0.5 rounded-full">
              <ArrowUpRight size={12} /> {totalFacturar} casos
            </span>
          </div>
          {!isManager && totalDescuentosPendientes > 0 && (
            <p className="text-[11px] text-amber-600 mt-2">
              Incluye descuentos pendientes por ${totalDescuentosPendientes.toLocaleString()}
            </p>
          )}
        </div>

        {/* Casos por estado según rol */}
        <div data-testid="stat-atendidos" className="min-w-0 bg-white p-2 rounded-2xl shadow-sm border border-slate-200 hover:shadow-lg transition-shadow duration-300">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-slate-500 text-xs font-medium">{isManager ? 'Casos Atendidos' : 'Mis Casos Atendidos'}</h3>
            <div className="p-1.5 bg-blue-100 text-blue-600 rounded-xl">
              <FileText size={16} />
            </div>
          </div>
          <p className="text-lg lg:text-[1.35rem] font-heading font-bold text-slate-800">{totalAtendidos}</p>
          <p className="text-[11px] text-slate-400 mt-0.5">{totalAtendidos} atendidos</p>
        </div>

        {/* 3. Casos a facturar */}
        <div data-testid="stat-total" className="min-w-0 bg-white p-2 rounded-2xl shadow-sm border border-slate-200 hover:shadow-lg transition-shadow duration-300">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-slate-500 text-xs font-medium">Casos a Facturar</h3>
            <div className="p-1.5 bg-primary/10 text-primary rounded-xl">
              <TrendingUp size={16} />
            </div>
          </div>
          <p className="text-lg lg:text-[1.35rem] font-heading font-bold text-slate-800">{totalFacturar}</p>
          <p className="text-[11px] text-slate-400 mt-0.5">Pendientes de pago</p>
        </div>

        {/* 4. Casos reclamados */}
        <div data-testid="stat-reclamados" className="min-w-0 bg-gradient-to-br from-amber-50 to-orange-50 p-2 rounded-2xl shadow-sm border border-amber-200 hover:shadow-lg transition-shadow duration-300">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-amber-700 text-xs font-medium">Casos Reclamados</h3>
            <div className="p-1.5 bg-amber-200 text-amber-700 rounded-xl">
              <AlertCircle size={16} />
            </div>
          </div>
          <p className="text-xl lg:text-[1.55rem] font-heading font-bold text-amber-800">{totalReclamados}</p>
          <p className="text-[11px] text-amber-600 mt-0.5">
            {totalReclamosPendientes} pendientes, {totalReclamosResueltos} resueltos
          </p>
        </div>
      </div>

      {!isManager && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 xl:flex-shrink-0">
          Regla de facturacion para casos a facturar: ASSA y Optima se cuentan a 15 dias; Regional y Fedpa a 1 mes.
        </div>
      )}

      {/* Loans Summary Card - Only for admin */}
      {isManager && loans.length > 0 && (
        <div className="min-w-0 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl shadow-sm border border-amber-200 p-2.5 xl:flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-xs font-bold text-amber-800">Préstamos Pendientes</h3>
            </div>
            <div className="p-1.5 bg-amber-200 text-amber-700 rounded-xl">
              <DollarSign size={20} />
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-1.5">
            <div className="min-w-0 bg-white rounded-xl p-2 border border-amber-200">
              <p className="mb-0.5 text-[10px] text-slate-600">Total Pendiente</p>
              <p className="text-sm font-bold text-amber-700">
                ${pendingLoans.reduce((sum, l) => sum + l.amount, 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="min-w-0 bg-white rounded-xl p-2 border border-amber-200">
              <p className="mb-0.5 text-[10px] text-slate-600">Préstamos Activos</p>
              <p className="text-sm font-bold text-amber-700">
                {pendingLoans.length}
              </p>
            </div>
            <div className="min-w-0 bg-white rounded-xl p-2 border border-emerald-200">
              <p className="mb-0.5 text-[10px] text-slate-600">Liquidados</p>
              <p className="text-sm font-bold text-emerald-700">
                ${paidLoans.reduce((sum, l) => sum + l.amount, 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>
      )}

      {isManager && (
        <div className="flex min-w-0 flex-col gap-2 rounded-2xl border border-indigo-100 bg-white px-3 py-2 shadow-sm xl:flex-shrink-0 xl:flex-row xl:items-center">
          <div className="flex shrink-0 items-center gap-2 xl:w-36">
            <div className="rounded-lg bg-indigo-100 p-1.5 text-indigo-600">
              <Clock size={14} />
            </div>
            <div className="min-w-0">
              <h3 className="text-xs font-bold text-slate-800">Horarios de hoy</h3>
              <p className="text-[9px] capitalize text-slate-400">
                {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })}
              </p>
            </div>
          </div>

          {todayScheduleItems.length > 0 ? (
            <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
              {todayScheduleItems.slice(0, 3).map((item) => (
                <div
                  key={`${item.userId}-${item.schedule}`}
                  className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5"
                >
                  <span className="truncate text-[11px] font-semibold text-slate-700" title={item.name}>
                    {item.name}
                  </span>
                  <span className="shrink-0 rounded-md bg-white px-1.5 py-0.5 text-[9px] font-semibold text-indigo-700">
                    {item.schedule}
                  </span>
                </div>
              ))}
              {todayScheduleItems.length > 3 && (
                <button
                  type="button"
                  onClick={() => setShowTodaySchedules(true)}
                  className="shrink-0 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-[10px] font-bold text-white transition hover:bg-indigo-700"
                >
                  +{todayScheduleItems.length - 3} más
                </button>
              )}
            </div>
          ) : (
            <p className="min-w-0 flex-1 truncate text-[11px] text-slate-400">
              No hay técnicos con horario asignado para hoy.
            </p>
          )}
        </div>
      )}

      {isManager && showTodaySchedules && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm"
          onMouseDown={() => setShowTodaySchedules(false)}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="rounded-lg bg-indigo-100 p-2 text-indigo-600"><Clock size={16} /></div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800">Horarios de hoy</h3>
                  <p className="text-[10px] capitalize text-slate-500">
                    {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowTodaySchedules(false)}
                className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Cerrar horarios de hoy"
              >
                <X size={17} />
              </button>
            </div>
            <div className="max-h-[60vh] space-y-1.5 overflow-y-auto p-3">
              {todayScheduleItems.map((item) => (
                <div
                  key={`popup-${item.userId}-${item.schedule}`}
                  className="flex items-center justify-between gap-4 rounded-xl bg-slate-50 px-3 py-2.5"
                >
                  <span className="min-w-0 truncate text-xs font-semibold text-slate-700">{item.name}</span>
                  <span className="shrink-0 rounded-lg bg-white px-2 py-1 text-[10px] font-bold text-indigo-700 shadow-sm">
                    {item.schedule}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Charts Section */}
      <div className={`grid grid-cols-1 gap-5 ${isManager ? 'xl:grid-cols-3 xl:flex-1 xl:min-h-0' : 'xl:flex-none'}`}>
        {/* Main Chart */}
        <div className={`min-w-0 bg-white pr-2 pb-1.5 pt-2 pl-3 rounded-2xl shadow-sm border border-slate-200 overflow-hidden ${isManager ? 'xl:col-span-2 xl:h-52 xl:min-h-0 xl:flex xl:flex-col' : ''}`}>
          <div className="flex items-center justify-between mb-1">
            <div>
              <h3 className="text-xs font-bold text-slate-800">
                {isManager ? 'Resumen de Expedientes' : 'Mis Expedientes'}
              </h3>
              <p className="text-xs text-slate-500">Distribución por estado</p>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
              <Receipt size={12} />
              <span>{dashboardSummary.uploadedServiceCount || 0} servicios subidos</span>
            </div>
          </div>
          
          {barData.length > 0 ? (
            <div className={`h-20 sm:h-24 min-w-0 ${isManager ? 'xl:flex-1 xl:min-h-0' : 'xl:h-28'}`}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#64748b', fontSize: 12 }} 
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#64748b', fontSize: 12 }} 
                  />
                  <Tooltip 
                    cursor={{ fill: '#f8fafc' }} 
                    formatter={(value) => [value, 'Cantidad']}
                    contentStyle={{ 
                      borderRadius: '12px', 
                      border: 'none', 
                      boxShadow: '0 10px 25px -5px rgb(0 0 0 / 0.1)' 
                    }} 
                  />
                  <Bar dataKey="count" radius={[8, 8, 0, 0]} barSize={34} fill="#4f46e5" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className={`h-20 sm:h-24 flex items-center justify-center text-slate-400 ${isManager ? 'xl:flex-1' : 'xl:h-28'}`}>
              <div className="text-center">
                <FileText size={36} className="mx-auto mb-2 opacity-30" />
                <p>No hay expedientes {isManager ? '' : 'asignados'}</p>
                <p className="text-xs mt-1">{isManager ? 'Importa casos para comenzar' : 'Espera a que te asignen casos'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Different for Admin vs Collaborator */}
        {isManager ? (
          // Admin: Show technician stats
          <div data-testid="technician-stats" className="min-w-0 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white pr-2 pb-1.5 pt-2 pl-3 rounded-2xl shadow-xl relative overflow-hidden xl:h-52 xl:min-h-0 xl:flex xl:flex-col">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/20 rounded-full blur-3xl"></div>
            
            <div className="relative mb-1.5 flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl shadow-lg">
                  <Users className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-xs font-bold">Por Técnico</h3>
                  <p className="truncate text-[11px] text-slate-400">Top 3 por servicios</p>
                </div>
              </div>
              <select
                value={selectedTechnicianMonth}
                onChange={(event) => setSelectedTechnicianMonth(event.target.value)}
                disabled={technicianMonthOptions.length === 0}
                aria-label="Mes del Top 3 de técnicos"
                className="h-7 max-w-[8.5rem] shrink-0 rounded-lg border border-white/15 bg-white/10 px-2 text-[10px] font-medium text-white outline-none focus:border-blue-300 disabled:opacity-50"
              >
                {technicianMonthOptions.length === 0 ? (
                  <option value="" className="text-slate-900">Sin datos</option>
                ) : (
                  technicianMonthOptions.map((monthKey) => (
                    <option key={monthKey} value={monthKey} className="text-slate-900">
                      {formatShortMonthLabel(monthKey)}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="space-y-1 relative xl:flex-1 xl:min-h-0 xl:overflow-auto">
              {technicianData.length > 0 ? (
                technicianData.map((tech, idx) => (
                  <div
                    key={`${tech.name}-${idx}`}
                    className="bg-white/10 backdrop-blur-sm px-2 py-2 rounded-xl border border-white/5"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-xs">{tech.name}</span>
                      <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full">
                        {tech.count} servicio{tech.count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-400 text-center py-8">No hay servicios registrados en este mes</p>
              )}
            </div>
          </div>
        ) : null}

        <div className={`min-w-0 bg-white p-4 rounded-2xl shadow-sm border border-slate-200 overflow-hidden ${isManager ? 'xl:col-span-3' : ''}`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-xs font-bold text-slate-800">Servicios por categoría</h3>
              <p className="text-xs text-slate-500">
                {selectedServiceMonth ? formatMonthLabel(selectedServiceMonth) : 'Sin mes disponible'}
              </p>
            </div>
            <select
              value={selectedServiceMonth}
              onChange={(e) => setSelectedServiceMonth(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary sm:w-56"
              disabled={serviceMonthOptions.length === 0}
            >
              {serviceMonthOptions.length === 0 ? (
                <option value="">Sin datos</option>
              ) : (
                serviceMonthOptions.map((monthKey) => (
                  <option key={monthKey} value={monthKey}>{formatMonthLabel(monthKey)}</option>
                ))
              )}
            </select>
          </div>

          {monthlyServiceData.length > 0 ? (
            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr] lg:items-center">
              <div className="relative h-64 min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={monthlyServiceData}
                      dataKey="count"
                      nameKey="name"
                      innerRadius={72}
                      outerRadius={104}
                      paddingAngle={2}
                      stroke="#ffffff"
                      strokeWidth={3}
                    >
                      {monthlyServiceData.map((service, index) => (
                        <Cell key={service.name} fill={SERVICE_COLORS[index % SERVICE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`${value} servicio${value !== 1 ? 's' : ''}`, 'Cantidad']}
                      contentStyle={{
                        borderRadius: '12px',
                        border: 'none',
                        boxShadow: '0 10px 25px -5px rgb(0 0 0 / 0.1)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-heading font-bold text-slate-800">{monthlyServiceTotal}</span>
                  <span className="text-xs font-medium text-slate-500">servicios</span>
                </div>
              </div>

              <div className="min-w-0">
                <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
                  <span>{monthlyServiceData.length} categoría{monthlyServiceData.length !== 1 ? 's' : ''}</span>
                  <span>Total: {monthlyServiceTotal}</span>
                </div>
                <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                  {monthlyServiceData.map((service, index) => {
                    const percentage = monthlyServiceTotal > 0
                      ? Math.round((service.count / monthlyServiceTotal) * 100)
                      : 0;

                    return (
                      <div key={service.name} className="flex min-w-0 items-center gap-3 rounded-xl bg-slate-50 px-3 py-2">
                        <span
                          className="h-3 w-3 shrink-0 rounded-full"
                          style={{ backgroundColor: SERVICE_COLORS[index % SERVICE_COLORS.length] }}
                        />
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700" title={service.name}>
                          {service.name}
                        </span>
                        <span className="shrink-0 text-xs text-slate-500">{percentage}%</span>
                        <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-xs font-bold text-slate-700 shadow-sm">
                          {service.count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <Receipt size={36} className="mx-auto mb-2 opacity-30" />
                <p>No hay servicios en este mes</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { currentUser, isManager, users } = useAuth();
  return isManager
    ? <AdminDashboard currentUser={currentUser} users={users} />
    : <CollaboratorDashboard currentUser={currentUser} users={users} />;
};

export default Dashboard;
